// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
import { useCallback, useEffect, useRef } from 'react';

import { getWindow } from '@change-corgi/core/window';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { getNavigatorShareInterface, isShareCancellation } from '#src/app/shared/share/utils';

type ShareFileMethod = 'share_sheet' | 'download';

/** Revoking right after the click can cancel the download in some browsers. */
const REVOKE_OBJECT_URL_DELAY_MS = 10_000;

/**
 * How long a share blocks another tap, even if `navigator.share` never settles: long enough to stop a
 * double tap before a slow device shows the sheet, short enough that a stuck share does not leave Save
 * unusable. The share stays in flight after it.
 */
const TAP_GUARD_TIMEOUT_MS = 5_000;

/**
 * In-app browsers such as Facebook on Android report `canShare({ files })` as true, then reject every
 * share at once with this `AbortError`, because the WebView has no share service behind it.
 */
const SHARE_SERVICE_UNAVAILABLE_MESSAGE = 'Internal error: could not connect to Web Share interface.';

/** Whether the browser rejected the share at once, inside the tap, so a download can answer the tap instead. */
function isImmediateShareRejection(error: unknown): boolean {
	if (!(error instanceof Error)) return false;
	return (
		error.name === 'InvalidStateError' ||
		(error.name === 'AbortError' && error.message === SHARE_SERVICE_UNAVAILABLE_MESSAGE)
	);
}

/**
 * Calls `onReturn` once the page is visible again after being hidden, or the window regains focus
 * after losing it. Returns the cleanup.
 */
function watchForReturnFromShareSheet(onReturn: () => void): () => void {
	const win = getWindow();
	const { document } = win;
	let wasHidden = false;
	let wasBlurred = false;

	const onVisibilityChange = () => {
		if (document.visibilityState === 'hidden') {
			wasHidden = true;
		} else if (wasHidden) {
			onReturn();
		}
	};
	const onBlur = () => {
		wasBlurred = true;
	};
	const onFocus = () => {
		if (wasBlurred) onReturn();
	};

	document.addEventListener('visibilitychange', onVisibilityChange);
	win.addEventListener('blur', onBlur);
	win.addEventListener('focus', onFocus);

	return () => {
		document.removeEventListener('visibilitychange', onVisibilityChange);
		win.removeEventListener('blur', onBlur);
		win.removeEventListener('focus', onFocus);
	};
}

function downloadFile(file: File): void {
	const win = getWindow();
	const url = win.URL.createObjectURL(file);
	const anchor = win.document.createElement('a');
	anchor.href = url;
	anchor.download = file.name;
	anchor.style.display = 'none';
	win.document.body.appendChild(anchor);
	anchor.click();
	win.document.body.removeChild(anchor);
	win.setTimeout(() => win.URL.revokeObjectURL(url), REVOKE_OBJECT_URL_DELAY_MS);
}

type UseShareFileResult = {
	/**
	 * Opens the native share sheet, or downloads where the browser cannot share files. Call it straight
	 * from the tap handler: iOS rejects a share after any `await`. Returns `null` when an earlier share
	 * still blocks taps.
	 */
	shareFile: (file: File) => ShareFileMethod | null;
	/** Whether the latest share's sheet may still be open. A function so event listeners never read a stale value. */
	isShareInFlight: () => boolean;
};

/** The share in flight: whether it still blocks taps, and how to stop its listeners and timer. */
type PendingShare = { blocksTaps: boolean; stop: () => void };

/**
 * Delivers a prebuilt file through the share sheet, with a download fallback.
 *
 * `navigator.share` sometimes never settles, so a share also ends when the supporter is back from the
 * sheet. A tap after `TAP_GUARD_TIMEOUT_MS` replaces the tracked share. While the stuck share still
 * holds the browser's share slot, the browser rejects the new one with `InvalidStateError`, and the
 * file downloads instead. It also downloads where the browser claims it can share files but has no
 * share service, as in some in-app browsers.
 *
 * Any other failure is reported (a cancel is not), even when it arrives late, and not retried as a
 * download: the sheet may already have shown, and the tap's user activation is spent.
 */
export function useShareFile(): UseShareFileResult {
	const { errorReporter } = useUtilityContext();
	const pendingRef = useRef<PendingShare | null>(null);

	useEffect(
		() => () => {
			pendingRef.current?.stop();
			pendingRef.current = null;
		},
		[],
	);

	const shareFile = useCallback(
		(file: File): ShareFileMethod | null => {
			if (pendingRef.current?.blocksTaps) return null;

			const files = [file];
			const navigatorShare = getNavigatorShareInterface();
			if (!navigatorShare?.canShare({ files })) {
				downloadFile(file);
				return 'download';
			}

			const win = getWindow();
			const pending: PendingShare = { blocksTaps: true, stop: () => {} };
			/** Ends this share. `false` when it already ended (released, replaced, or unmounted). */
			const finish = (): boolean => {
				if (pendingRef.current !== pending) return false;
				pending.stop();
				pendingRef.current = null;
				return true;
			};
			const stopWatching = watchForReturnFromShareSheet(finish);
			const tapGuardTimeoutId = win.setTimeout(() => {
				pending.blocksTaps = false;
			}, TAP_GUARD_TIMEOUT_MS);
			pending.stop = () => {
				stopWatching();
				win.clearTimeout(tapGuardTimeoutId);
			};
			pendingRef.current?.stop();
			pendingRef.current = pending;

			/** Runs synchronously up to its first `await`, so `navigator.share` stays inside the tap's user activation. */
			const share = async () => {
				try {
					await navigatorShare.share({ files });
					finish();
				} catch (error) {
					const wasCurrent = finish();
					if (error instanceof Error && isShareCancellation(error)) return;
					if (isImmediateShareRejection(error)) {
						// An earlier share that never settled still holds the share slot, or the browser has no share
						// service. Either way it rejects this one at once, so a download still answers the tap.
						if (wasCurrent) downloadFile(file);
						return;
					}
					void errorReporter.report({
						error: new Error('Could not share the file', { cause: error }),
						context: { feature: 'psf_promote_or_share_qr_code_experiment' },
					});
				}
			};
			void share();
			return 'share_sheet';
		},
		[errorReporter],
	);

	const isShareInFlight = useCallback(() => pendingRef.current !== null, []);

	return { shareFile, isShareInFlight };
}
